import PropTypes from "prop-types";
import { useCallback, useState } from "react";
import { convertMilliSecondsToTime } from "app/utils/utils";
import {
	flightsFilterValuesShape,
	quotationFlightShape,
	quotationPropTypes,
} from "app/utils/propTypes";
import { FormattedMessage, useIntl } from "react-intl";
import classNames from "classnames";
import isEqual from "lodash/isEqual";
import Collapse from "app/components/ui/collapse/Collapse";
import CabinsFilter from "./CabinsFilter.jsx";
import FilterPredicates from "./FilterPredicates.jsx";
import { sendTagOnFlightFilter, sendTagOnFlightResetFilter } from "app/utils/analytics";
import { Slider } from "app/components/ui/Slider/Slider";
import "./FlightsFilterSort.scss";
import FlightsFilterMarketingBannerContainer from "app/pages/Booking/Quotation/Flight/filter/FlightsFilterMarketingBannerContainer";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import FlightFilterButton from "./FlightFilterButton.jsx";
import { Button } from "app/components/ui/Button/Button";

const FILTER_KEYS = {
	DEPARTURE_AIRPORT: "departureAirport",
	ARRIVAL_AIRPORT: "arrivalAirport",
	CABIN: "cabin",
	AIRLINE: "airline",
	LUGGAGE: "luggage",
	STOP: "stop",
	INBOUND_DURATION: "inboundDuration",
	OUTBOUND_DURATION: "outboundDuration",
	INBOUND_TAKE_OFF_TIME: "inboundTakeOffTime",
	OUTBOUND_TAKE_OFF_TIME: "outboundTakeOffTime",
	STOPOVER_TAKE_OFF_TIME: "stopoverTakeOffTime",
	UPGRADE_PRICE: "upgradePrice",
};

const ExpandLessIcon = ({ isActive = false }) => (
	<svg
		className={classNames("filter-sort__mobile-section-chevron", {
			"filter-sort__mobile-section-chevron--open": isActive,
		})}
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		aria-hidden="true"
	>
		<path
			d="M7.41 14.59L12 10L16.59 14.59L18 13.17L12 7.17L6 13.17L7.41 14.59Z"
			fill="currentColor"
		/>
	</svg>
);

const renderMobileCollapseSection = (
	sectionKey,
	messageId,
	isSelected,
	content,
	boxOptions = false
) => ({
	key: sectionKey,
	label: (
		<div
			className={classNames("filter-sort__mobile-section-header", {
				"filter-sort__mobile-section-header--box-options": boxOptions,
			})}
		>
			<Typography variant={TYPOGRAPHY_VARIANTS.LARGE} isBold>
				<FormattedMessage id={messageId}>
					{message => `${message}${isSelected ? " (1)" : ""}`}
				</FormattedMessage>
			</Typography>
		</div>
	),
	children: content,
	className: classNames("filter-sort__mobile-section", {
		"filter-sort__mobile-section--box-options": boxOptions,
	}),
	isSelected,
});

ExpandLessIcon.propTypes = {
	isActive: PropTypes.bool,
};

const FlightsFilterSort = ({
	travelByTrainAvailable,
	quotation,
	filterValues,
	setFlightsFilterBy,
	filtersCount = 0,
	resetFlightsFilter,
	resetFlightsPageSize,
	minUpgradePricesByCabin,
	includedFlight,
	shouldShowFlightFilters,
	currencyCode,
	hideCabins = false,
	isMobileDrawer = false,
	subtitle,
}) => {
	const intl = useIntl();
	const [selectedCabin, setSelectedCabin] = useState(undefined);
	const filterBy = useCallback(predicate => setFlightsFilterBy(predicate), []);

	const onClickCabin = useCallback(cabin => {
		let predicate = { [FILTER_KEYS.CABIN]: cabin };
		filterBy(predicate);
		sendTagOnFlightFilter(FILTER_KEYS.CABIN, cabin);
		setSelectedCabin(cabin);
		resetFlightsPageSize();
	}, []);

	const isStopFilterSelected = quotation.filterFlightsBy[FILTER_KEYS.STOP] !== undefined;
	const isAirlineFilterSelected = quotation.filterFlightsBy[FILTER_KEYS.AIRLINE] !== undefined;
	const isLuggageFilterSelected = quotation.filterFlightsBy[FILTER_KEYS.LUGGAGE] !== undefined;
	const isDurationFilterSelected =
		quotation.filterFlightsBy[FILTER_KEYS.INBOUND_DURATION]?.min !== undefined &&
		quotation.filterFlightsBy[FILTER_KEYS.INBOUND_DURATION]?.max !== undefined &&
		quotation.filterFlightsBy[FILTER_KEYS.OUTBOUND_DURATION]?.min !== undefined &&
		quotation.filterFlightsBy[FILTER_KEYS.OUTBOUND_DURATION]?.max !== undefined &&
		(!isEqual(
			quotation.filterFlightsBy[FILTER_KEYS.INBOUND_DURATION],
			filterValues[FILTER_KEYS.INBOUND_DURATION]
		) ||
			!isEqual(
				quotation.filterFlightsBy[FILTER_KEYS.OUTBOUND_DURATION],
				filterValues[FILTER_KEYS.OUTBOUND_DURATION]
			));
	const isTakeOffTimeFilterSelected =
		quotation.filterFlightsBy[FILTER_KEYS.INBOUND_TAKE_OFF_TIME] !== undefined &&
		quotation.filterFlightsBy[FILTER_KEYS.OUTBOUND_TAKE_OFF_TIME] !== undefined &&
		(!isEqual(
			quotation.filterFlightsBy[FILTER_KEYS.INBOUND_TAKE_OFF_TIME],
			filterValues[FILTER_KEYS.INBOUND_TAKE_OFF_TIME]
		) ||
			!isEqual(
				quotation.filterFlightsBy[FILTER_KEYS.OUTBOUND_TAKE_OFF_TIME],
				filterValues[FILTER_KEYS.OUTBOUND_TAKE_OFF_TIME]
			));
	const isPriceFilterSelected =
		quotation.filterFlightsBy[FILTER_KEYS.UPGRADE_PRICE] !== undefined &&
		quotation.filterFlightsBy[FILTER_KEYS.UPGRADE_PRICE] !==
			filterValues[FILTER_KEYS.UPGRADE_PRICE];

	const filterAirportButtonClassName = classNames("action__item", {
		"action__item--active":
			quotation.filterFlightsBy[FILTER_KEYS.DEPARTURE_AIRPORT] !== undefined ||
			quotation.filterFlightsBy[FILTER_KEYS.ARRIVAL_AIRPORT] !== undefined,
	});
	const filterStopButtonClassName = classNames("action__item", {
		"action__item--active": isStopFilterSelected,
	});
	const filterAirlineButtonClassName = classNames("action__item", {
		"action__item--active": isAirlineFilterSelected,
	});
	const filterLuggageButtonClassName = classNames("action__item", {
		"action__item--active": isLuggageFilterSelected,
	});
	const filterDurationButtonClassName = classNames("action__item", {
		"action__item--active": isDurationFilterSelected,
	});
	const filterTakeOffTimeButtonClassName = classNames("action__item", {
		"action__item--active": isTakeOffTimeFilterSelected,
	});
	const filterPriceButtonClassName = classNames("action__item", {
		"action__item--active": isPriceFilterSelected,
	});

	// Le composant react-popover ne bénéficie pas de react-intl. On doit donc générer les labels en dehors

	const getStopFilterLabel = stop => {
		if (stop === 0) {
			return intl.formatMessage({ id: "general.direct" });
		} else if (intl.formatPlural(stop) === "one") {
			return intl.formatMessage(
				{
					id: "general.stop.singular",
				},
				{
					value: stop,
				}
			);
		}

		return intl.formatMessage(
			{
				id: "general.stop.plural",
			},
			{
				value: stop,
			}
		);
	};

	const getLuggageFilterLabel = isLuggageIncluded => {
		if (isLuggageIncluded === true) {
			return intl.formatMessage({ id: "general.luggage.included" });
		}
		return intl.formatMessage({ id: "general.luggage.not.included" });
	};

	const shouldShowInboundDurationFilter =
		filterValues.inboundDuration.min !== filterValues.inboundDuration.max;
	const shouldShowOutboundDurationFilter =
		filterValues.outboundDuration.min !== filterValues.outboundDuration.max;
	const shouldShowDurationFilter =
		shouldShowInboundDurationFilter && shouldShowOutboundDurationFilter;

	const shouldShowOutboundTimeFilter =
		filterValues.outboundTakeOffTime.min !== filterValues.outboundTakeOffTime.max;
	const shouldShowInboundTimeFilter =
		filterValues.inboundTakeOffTime.min !== filterValues.inboundTakeOffTime.max;
	const shouldShowTimeFilter = shouldShowOutboundTimeFilter && shouldShowInboundTimeFilter;

	const shouldShowPriceFilter = filterValues.upgradePrice !== 0;
	const shouldShowStopFilter = filterValues?.stopsCount?.length > 1;
	const shouldShowAirlineFilter = filterValues.airline && filterValues.airline.length > 1;
	const shouldShowLuggageFilter = filterValues[FILTER_KEYS.LUGGAGE]?.length === 2;

	const createAirportFilter = options => {
		const { filterKey, values = [], activeValue, labelIds = {} } = options || {};

		if (!values || values.length <= 1) {
			return null;
		}

		const labelId = travelByTrainAvailable ? labelIds.train : labelIds.default;

		return (
			<FilterPredicates
				key={filterKey}
				label={intl.formatMessage({ id: labelId })}
				predicates={values}
				activeIfEqualTo={activeValue}
				onSelect={selectedValue => {
					const isActive = selectedValue === activeValue;
					const predicate = isActive ? undefined : selectedValue;
					filterBy({ [filterKey]: predicate });

					if (!isActive) {
						sendTagOnFlightFilter(filterKey, predicate);
					}
				}}
			/>
		);
	};

	const airportFilters = [
		createAirportFilter({
			filterKey: FILTER_KEYS.DEPARTURE_AIRPORT,
			values: filterValues?.departureAirport,
			activeValue: quotation.filterFlightsBy.departureAirport,
			labelIds: {
				train: "general.filter.departure.airport.gare.label",
				default: "general.filter.departure.airport.label",
			},
		}),
		createAirportFilter({
			filterKey: FILTER_KEYS.ARRIVAL_AIRPORT,
			values: filterValues?.arrivalAirport,
			activeValue: quotation.filterFlightsBy.arrivalAirport,
			labelIds: {
				train: "general.filter.arrival.airport.gare.label",
				default: "general.filter.arrival.airport.label",
			},
		}),
	].filter(Boolean);

	const shouldShowAirportFilter = airportFilters.length > 0;
	const shouldGroupAirportFilters = airportFilters.length > 1;
	const airportButtonLabelId = travelByTrainAvailable
		? "quotation.filter.multi.transport"
		: "quotation.flights.filter.airport";
	const upgradePriceLabel = intl.formatMessage({ id: "general.filter.upgrade.price.label" });
	const shouldUseCompactFiltersLayout =
		[
			shouldShowStopFilter,
			shouldShowDurationFilter,
			shouldShowTimeFilter,
			shouldShowAirlineFilter,
			shouldShowLuggageFilter,
			shouldShowPriceFilter,
			shouldShowAirportFilter,
		].filter(Boolean).length === 2;
	const shouldRenderSecondaryFilters = shouldShowPriceFilter || shouldShowAirportFilter;

	const durationFilterContent = (
		<div className="filter-predicates filter-predicates--slider">
			{shouldShowOutboundDurationFilter && (
				<div className="filter-predicates__slider-group">
					<Typography
						variant={TYPOGRAPHY_VARIANTS.REGULAR}
						className="filter-predicates__slider-label"
					>
						<FormattedMessage id="general.filter.outbound.duration.label" />
					</Typography>
					<Slider
						value={[
							quotation.filterFlightsBy.outboundDuration?.min ??
								filterValues.outboundDuration.min,
							quotation.filterFlightsBy.outboundDuration?.max ??
								filterValues.outboundDuration.max,
						]}
						step={1}
						formatValue={convertMilliSecondsToTime}
						min={filterValues.outboundDuration.min}
						max={filterValues.outboundDuration.max}
						onValueCommit={value => {
							const [minValue, maxValue] = value;
							filterBy({
								[FILTER_KEYS.OUTBOUND_DURATION]: {
									min: minValue,
									max: maxValue,
								},
							});

							sendTagOnFlightFilter(
								FILTER_KEYS.OUTBOUND_DURATION,
								`${convertMilliSecondsToTime(
									minValue
								)} - ${convertMilliSecondsToTime(maxValue)}`
							);
						}}
					/>
				</div>
			)}

			{shouldShowInboundDurationFilter && (
				<div className="filter-predicates__slider-group">
					<Typography
						variant={TYPOGRAPHY_VARIANTS.REGULAR}
						className="filter-predicates__slider-label"
					>
						<FormattedMessage id="general.filter.inbound.duration.label" />
					</Typography>
					<Slider
						value={[
							quotation.filterFlightsBy.inboundDuration?.min ??
								filterValues.inboundDuration.min,
							quotation.filterFlightsBy.inboundDuration?.max ??
								filterValues.inboundDuration.max,
						]}
						step={1}
						formatValue={convertMilliSecondsToTime}
						min={filterValues.inboundDuration.min}
						max={filterValues.inboundDuration.max}
						onValueCommit={value => {
							const [minValue, maxValue] = value;
							filterBy({
								[FILTER_KEYS.INBOUND_DURATION]: {
									min: minValue,
									max: maxValue,
								},
							});

							sendTagOnFlightFilter(
								FILTER_KEYS.INBOUND_DURATION,
								`${convertMilliSecondsToTime(
									minValue
								)} - ${convertMilliSecondsToTime(maxValue)}`
							);
						}}
					/>
				</div>
			)}
		</div>
	);

	const shouldShowStopoverTimeFilter =
		filterValues.stopoverTakeOffTime?.min !== undefined &&
		filterValues.stopoverTakeOffTime?.max !== undefined &&
		filterValues.stopoverTakeOffTime.min !== filterValues.stopoverTakeOffTime.max;

	const timeFilterContent = (
		<div className="filter-predicates filter-predicates--slider">
			{shouldShowOutboundTimeFilter && (
				<div className="filter-predicates__slider-group">
					<Typography
						variant={TYPOGRAPHY_VARIANTS.REGULAR}
						className="filter-predicates__slider-label"
					>
						<FormattedMessage id="general.filter.outbound.take.off.time.label" />
					</Typography>
					<Slider
						value={[
							quotation.filterFlightsBy.outboundTakeOffTime?.min ??
								filterValues.outboundTakeOffTime.min,
							quotation.filterFlightsBy.outboundTakeOffTime?.max ??
								filterValues.outboundTakeOffTime.max,
						]}
						step={1}
						formatValue={convertMilliSecondsToTime}
						min={filterValues.outboundTakeOffTime.min}
						max={filterValues.outboundTakeOffTime.max}
						onValueCommit={value => {
							filterBy({
								[FILTER_KEYS.OUTBOUND_TAKE_OFF_TIME]: {
									min: value[0],
									max: value[1],
								},
							});

							sendTagOnFlightFilter(
								FILTER_KEYS.OUTBOUND_TAKE_OFF_TIME,
								`${convertMilliSecondsToTime(
									value[0]
								)} - ${convertMilliSecondsToTime(value[1])}`
							);
						}}
					/>
				</div>
			)}

			{shouldShowStopoverTimeFilter && (
				<div className="filter-predicates__slider-group">
					<Typography
						variant={TYPOGRAPHY_VARIANTS.REGULAR}
						className="filter-predicates__slider-label"
					>
						<FormattedMessage id="general.filter.stopover.take.off.time.label" />
					</Typography>
					<Slider
						value={[
							quotation.filterFlightsBy.stopoverTakeOffTime?.min ??
								filterValues.stopoverTakeOffTime.min,
							quotation.filterFlightsBy.stopoverTakeOffTime?.max ??
								filterValues.stopoverTakeOffTime.max,
						]}
						step={1}
						formatValue={convertMilliSecondsToTime}
						min={filterValues.stopoverTakeOffTime.min}
						max={filterValues.stopoverTakeOffTime.max}
						onValueCommit={value => {
							filterBy({
								[FILTER_KEYS.STOPOVER_TAKE_OFF_TIME]: {
									min: value[0],
									max: value[1],
								},
							});

							sendTagOnFlightFilter(
								FILTER_KEYS.STOPOVER_TAKE_OFF_TIME,
								`${convertMilliSecondsToTime(
									value[0]
								)} - ${convertMilliSecondsToTime(value[1])}`
							);
						}}
					/>
				</div>
			)}

			{shouldShowInboundTimeFilter && (
				<div className="filter-predicates__slider-group">
					<Typography
						variant={TYPOGRAPHY_VARIANTS.REGULAR}
						className="filter-predicates__slider-label"
					>
						<FormattedMessage id="general.filter.inbound.take.off.time.label" />
					</Typography>
					<Slider
						value={[
							quotation.filterFlightsBy.inboundTakeOffTime?.min ??
								filterValues.inboundTakeOffTime.min,
							quotation.filterFlightsBy.inboundTakeOffTime?.max ??
								filterValues.inboundTakeOffTime.max,
						]}
						step={1}
						formatValue={convertMilliSecondsToTime}
						min={filterValues.inboundTakeOffTime.min}
						max={filterValues.inboundTakeOffTime.max}
						onValueCommit={value => {
							filterBy({
								[FILTER_KEYS.INBOUND_TAKE_OFF_TIME]: {
									min: value[0],
									max: value[1],
								},
							});

							sendTagOnFlightFilter(
								FILTER_KEYS.INBOUND_TAKE_OFF_TIME,
								`${convertMilliSecondsToTime(
									value[0]
								)} - ${convertMilliSecondsToTime(value[1])}`
							);
						}}
					/>
				</div>
			)}
		</div>
	);

	const airportFilterContent = shouldGroupAirportFilters ? (
		<div className="filter-sort__airport-group">{airportFilters}</div>
	) : (
		(airportFilters[0] ?? null)
	);

	const stopFilterContent = (
		<FilterPredicates
			predicates={filterValues.stopsCount}
			defaultValue={2}
			activeIfEqualTo={quotation.filterFlightsBy.stop}
			customiseLabelFunction={getStopFilterLabel}
			onSelect={stop => {
				const isActive = stop === quotation.filterFlightsBy.stop;
				const predicate = isActive ? undefined : stop;
				filterBy({ [FILTER_KEYS.STOP]: predicate });
				sendTagOnFlightFilter(FILTER_KEYS.STOP, predicate);
			}}
		/>
	);

	const airlineFilterContent = (
		<FilterPredicates
			predicates={filterValues.airline}
			activeIfEqualTo={quotation.filterFlightsBy.airline}
			onSelect={airline => {
				const isActive = airline === quotation.filterFlightsBy.airline;
				const predicate = isActive ? undefined : airline;
				filterBy({ [FILTER_KEYS.AIRLINE]: predicate });
				if (!isActive) {
					sendTagOnFlightFilter(FILTER_KEYS.AIRLINE, predicate);
				}
			}}
		/>
	);

	const luggageFilterContent = (
		<FilterPredicates
			predicates={filterValues[FILTER_KEYS.LUGGAGE]}
			activeIfEqualTo={quotation.filterFlightsBy.luggage}
			customiseLabelFunction={getLuggageFilterLabel}
			onSelect={luggage => {
				const isActive = luggage === quotation.filterFlightsBy.luggage;
				const predicate = isActive ? undefined : luggage;
				filterBy({ [FILTER_KEYS.LUGGAGE]: predicate });
				if (!isActive) {
					sendTagOnFlightFilter(FILTER_KEYS.LUGGAGE, predicate);
				}
			}}
		/>
	);

	const priceFilterContent = (
		<div className="filter-predicates filter-predicates--slider">
			<div className="filter-predicates__slider-group">
				<Typography
					variant={TYPOGRAPHY_VARIANTS.REGULAR}
					className="filter-predicates__slider-label"
				>
					{upgradePriceLabel}
				</Typography>
				<Slider
					value={[quotation.filterFlightsBy.upgradePrice ?? filterValues.upgradePrice]}
					step={1}
					min={0}
					formatValue={value =>
						intl.formatNumber(value, {
							style: "currency",
							currency: currencyCode,
							currencyDisplay: "symbol",
							minimumFractionDigits: 0,
							maximumFractionDigits: 2,
						})
					}
					max={filterValues.upgradePrice}
					onValueCommit={value => {
						filterBy({ [FILTER_KEYS.UPGRADE_PRICE]: value[0] });
						sendTagOnFlightFilter(FILTER_KEYS.UPGRADE_PRICE, value[0]);
					}}
				/>
			</div>
		</div>
	);

	const filtersLabel = intl.formatMessage({ id: "general.filter.label" });
	let displayedFiltersLabel = filtersLabel;
	if (filtersCount > 0) {
		displayedFiltersLabel = `${filtersLabel} (${filtersCount})`;
	}

	if (isMobileDrawer) {
		const departureAirportLabelId = travelByTrainAvailable
			? "general.filter.departure.airport.gare.label"
			: "general.filter.departure.airport.label";
		const arrivalAirportLabelId = travelByTrainAvailable
			? "general.filter.arrival.airport.gare.label"
			: "general.filter.arrival.airport.label";

		const items = [
			filterValues?.stopsCount?.length > 1 &&
				renderMobileCollapseSection(
					"stop",
					"quotation.flights.filter.stop",
					isStopFilterSelected,
					stopFilterContent,
					true
				),
			shouldShowDurationFilter &&
				renderMobileCollapseSection(
					"duration",
					"quotation.flights.filter.duration",
					isDurationFilterSelected,
					durationFilterContent
				),
			shouldShowTimeFilter &&
				renderMobileCollapseSection(
					"time",
					"quotation.flights.filter.time",
					isTakeOffTimeFilterSelected,
					timeFilterContent
				),
			filterValues.airline?.length > 1 &&
				renderMobileCollapseSection(
					"airline",
					"quotation.flights.filter.airline",
					isAirlineFilterSelected,
					airlineFilterContent,
					true
				),
			filterValues[FILTER_KEYS.LUGGAGE]?.length === 2 &&
				renderMobileCollapseSection(
					"luggage",
					"quotation.flights.filter.luggage",
					isLuggageFilterSelected,
					luggageFilterContent,
					true
				),
			shouldShowPriceFilter &&
				renderMobileCollapseSection(
					"price",
					"quotation.flights.filter.price",
					isPriceFilterSelected,
					priceFilterContent
				),
			filterValues?.departureAirport?.length > 1 &&
				renderMobileCollapseSection(
					"departure-airport",
					departureAirportLabelId,
					quotation.filterFlightsBy.departureAirport !== undefined,
					<FilterPredicates
						predicates={filterValues.departureAirport}
						activeIfEqualTo={quotation.filterFlightsBy.departureAirport}
						onSelect={selectedValue => {
							const isActive =
								selectedValue === quotation.filterFlightsBy.departureAirport;
							const predicate = isActive ? undefined : selectedValue;
							filterBy({
								[FILTER_KEYS.DEPARTURE_AIRPORT]: predicate,
							});

							if (!isActive) {
								sendTagOnFlightFilter(FILTER_KEYS.DEPARTURE_AIRPORT, predicate);
							}
						}}
					/>,
					true
				),
			filterValues?.arrivalAirport?.length > 1 &&
				renderMobileCollapseSection(
					"arrival-airport",
					arrivalAirportLabelId,
					quotation.filterFlightsBy.arrivalAirport !== undefined,
					<FilterPredicates
						predicates={filterValues.arrivalAirport}
						activeIfEqualTo={quotation.filterFlightsBy.arrivalAirport}
						onSelect={selectedValue => {
							const isActive =
								selectedValue === quotation.filterFlightsBy.arrivalAirport;
							const predicate = isActive ? undefined : selectedValue;
							filterBy({
								[FILTER_KEYS.ARRIVAL_AIRPORT]: predicate,
							});

							if (!isActive) {
								sendTagOnFlightFilter(FILTER_KEYS.ARRIVAL_AIRPORT, predicate);
							}
						}}
					/>,
					true
				),
		].filter(Boolean);

		// on veut pre ouvrir les items des filtres qui ont été activés. Par défaut, on ouvre le premier
		const defaultActiveKey = [
			...new Set(
				[items[0]?.key, ...items.filter(i => i.isSelected).map(i => i.key)].filter(Boolean)
			),
		];
		const collapseItems = items.map(({ isSelected, ...item }) => item);

		// permet de forcer le remount de Collapse chaque fois que defaultActiveKey change
		// (c’est-à-dire lorsque le filtre est appliqué ou réinitialisé).
		// Sans cela, Collapse conserve son état interne — donc si l’utilisateur ouvre manuellement "time",
		// puis ferme et rouvre le drawer sans que le composant soit démonté, les deux sections restent ouvertes
		const collapseKey = defaultActiveKey.join(",");

		return (
			<div className="filter-sort filter-sort--mobile-drawer">
				<Collapse
					key={collapseKey}
					className="filter-sort__mobile-collapse"
					defaultActiveKey={defaultActiveKey}
					expandIcon={({ isActive }) => <ExpandLessIcon isActive={isActive} />}
					items={collapseItems}
				/>
			</div>
		);
	}

	return (
		<div className="filter-sort">
			<FlightsFilterMarketingBannerContainer
				className="filter-sort__cabin-banner"
				onClickCabin={onClickCabin}
			/>
			{subtitle}
			{shouldShowFlightFilters && (
				<div className="filter-sort__buttons">
					<div className="filter-sort__filter">
						<Typography
							variant={TYPOGRAPHY_VARIANTS.REGULAR}
							className="filter-sort__counter"
						>
							{displayedFiltersLabel}
						</Typography>
					</div>

					{filtersCount > 0 ? (
						<Button
							variant="borderless"
							size="sm"
							onClick={() => {
								resetFlightsFilter(filterValues);
								resetFlightsPageSize();
								sendTagOnFlightResetFilter();
							}}
							className="action__reset"
						>
							<Typography
								variant={TYPOGRAPHY_VARIANTS.REGULAR}
								isBold
								className="action__reset-label"
							>
								{intl.formatMessage({ id: "flight.filter.sort.reset" })}
							</Typography>
						</Button>
					) : (
						false
					)}
				</div>
			)}

			{shouldShowFlightFilters && (
				<div
					className={classNames("filter-sort__actions", {
						"filter-sort__actions--compact": shouldUseCompactFiltersLayout,
					})}
				>
					<div className="action__list">
						{shouldShowStopFilter && (
							<FlightFilterButton
								className={filterStopButtonClassName}
								label={intl.formatMessage({ id: "quotation.flights.filter.stop" })}
								floatingContent={stopFilterContent}
								floatingOffsetMobile={[0, -10]}
							/>
						)}
						{shouldShowDurationFilter && (
							<FlightFilterButton
								className={filterDurationButtonClassName}
								label={intl.formatMessage({
									id: "quotation.flights.filter.duration",
								})}
								floatingContent={durationFilterContent}
								dismissOnFloatingClick={false}
							/>
						)}

						{shouldShowTimeFilter && (
							<FlightFilterButton
								className={filterTakeOffTimeButtonClassName}
								label={intl.formatMessage({ id: "quotation.flights.filter.time" })}
								floatingContent={timeFilterContent}
								dismissOnFloatingClick={false}
							/>
						)}

						{shouldShowAirlineFilter && (
							<FlightFilterButton
								className={filterAirlineButtonClassName}
								label={intl.formatMessage({
									id: "quotation.flights.filter.airline",
								})}
								floatingContent={airlineFilterContent}
								floatingOffsetMobile={[0, -10]}
							/>
						)}

						{shouldShowLuggageFilter && (
							<FlightFilterButton
								className={filterLuggageButtonClassName}
								label={intl.formatMessage({
									id: "quotation.flights.filter.luggage",
								})}
								floatingContent={luggageFilterContent}
								floatingOffsetMobile={[0, -10]}
							/>
						)}
					</div>
					{shouldRenderSecondaryFilters && (
						<div className="action__list">
							{shouldShowPriceFilter && (
								<FlightFilterButton
									className={filterPriceButtonClassName}
									label={intl.formatMessage({
										id: "quotation.flights.filter.price",
									})}
									floatingContent={priceFilterContent}
									dismissOnFloatingClick={false}
								/>
							)}

							{shouldShowAirportFilter && (
								<FlightFilterButton
									className={filterAirportButtonClassName}
									label={intl.formatMessage({ id: airportButtonLabelId })}
									floatingContent={airportFilterContent}
								/>
							)}
						</div>
					)}
				</div>
			)}

			{!hideCabins && filterValues.cabin && filterValues.cabin.length > 1 && (
				<div className="filter-sort__cabins">
					<CabinsFilter
						includedFlight={includedFlight}
						minUpgradePricesByCabin={minUpgradePricesByCabin}
						cabins={filterValues.cabin}
						selectedCabin={selectedCabin || quotation.filterFlightsBy.cabin}
						onSelect={onClickCabin}
					/>
				</div>
			)}
		</div>
	);
};

FlightsFilterSort.propTypes = {
	travelByTrainAvailable: PropTypes.bool,
	currencyCode: PropTypes.string,
	includedFlight: quotationFlightShape,
	filterValues: flightsFilterValuesShape,
	filtersCount: PropTypes.number,
	quotation: quotationPropTypes,
	resetFlightsPageSize: PropTypes.func,
	setFlightsFilterBy: PropTypes.func,
	resetFlightsFilter: PropTypes.func,
	shouldShowFlightFilters: PropTypes.bool,
	minUpgradePricesByCabin: PropTypes.object,
	hideCabins: PropTypes.bool,
	isMobileDrawer: PropTypes.bool,
	subtitle: PropTypes.node,
};

export default FlightsFilterSort;
